import { Check, X } from "lucide-react"
import { Button } from "../../../components/button"
import { PERMIT_TYPES, STATUS_IDS, USER_ROLES_IDS } from "../../../constants/common"
import useAuthStore from "../../../store/Auth"
import { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal"
import RejectPermitModal from "../../../components/modal/RejectPermitModal"
import toast from "react-hot-toast"
import { useAddUpdateIssuanceRenewalIdForStaffFromContractedCompanies } from "../../../hooks/permit/mutation"
import { APP_ROUTES } from "../../../routes"
import { useLocation, useNavigate } from "react-router-dom"
import { UseFormHandleSubmit } from "react-hook-form"
import { fileToBase64, formattedDateTime } from "../../../lib/functions"

interface FormData {
    contractorEmployeeName: string;
    nationality: string;
    nationalId: string;
    nationalIdExpiryDate: string;
    dob: string;
    contractorsCompanyName: string;
    department: string;
    jobPosition: string;
    workStartDate: string;
    residentialAddress: string;
    contactNumber: string;
    cardValidityFrom: string;
    cardValidityUntil: string;

    uploadPersonalPhoto?: any;
    uploadCopyOfNationalId?: any;
    uploadContractorsAuthorizationLetter?: any;
}

interface ActionButtonGroupProps {
    issuanceRenewalId: any;
    isFieldsDisabled: boolean;
    isPermitApprovedByNextApprover: boolean;
    handleSubmit: UseFormHandleSubmit<{
        uploadPersonalPhoto?: any;
        uploadCopyOfNationalId?: any;
        uploadContractorsAuthorizationLetter?: any;

        contractorEmployeeName: string;
        nationality: string;
        nationalId: string;
        nationalIdExpiryDate: string;
        dob: string;
        contractorsCompanyName: string;
        department: string;
        jobPosition: string;
        workStartDate: string;
        residentialAddress: string;
        contactNumber: string;
        cardValidityFrom: string;
        cardValidityUntil: string;
    }, undefined>
}

const ActionButtonGroup = ({
    issuanceRenewalId,
    isFieldsDisabled,
    handleSubmit,
    isPermitApprovedByNextApprover,
}: ActionButtonGroupProps) => {
    const { user } = useAuthStore()

    const { t } = useTranslation()

    const navigate = useNavigate()

    const location = useLocation()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);
    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);

    const isApprovePage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES,
        [location.pathname],
    );

    const {
        mutateAsync: AddUpdateIssuanceRenewalIdForStaffFromContractedCompanies,
        isPending: isAddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesLoading,
    } = useAddUpdateIssuanceRenewalIdForStaffFromContractedCompanies();

    const isApproveDisabled = useMemo(
        () =>
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (issuanceRenewalId?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== issuanceRenewalId?.n_PermitNextApproverRoleID),
        [
            issuanceRenewalId?.n_ApprovalStatus,
            issuanceRenewalId?.n_PermitNextApproverRoleID,
            user?.n_RoleID,
        ]
    );

    const onApprove = useCallback(
        async () => {
            if (isApproveDisabled) return;

            setIsApprovePermitModalOpen(true);
        },
        [isApproveDisabled, t]
    );

    const isRejectDisabled = useMemo(
        () =>
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            issuanceRenewalId?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [issuanceRenewalId?.n_ApprovalStatus]
    );

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, issuanceRenewalId?.permitEndDate, t]);

    const onSubmit = useCallback(async (data: FormData) => {
        if (new Date(data.workStartDate) < new Date(data.cardValidityFrom) || new Date(data.workStartDate) > new Date(data.cardValidityUntil)) {
            toast.error(t("cardInvalidOnWorkDates"));
            return;
        } else if (new Date(data.cardValidityFrom) > new Date(data.cardValidityUntil)) {
            toast.error(t("cardValidityFromMustBeBeforeValidityUntil"));
            return;
        } else if (new Date(data.nationalIdExpiryDate) < new Date(data.workStartDate)) {
            toast.error(t("nationalIdWillExpireBeforeWorkStartDate"));
            return;
        }

        try {
            const body = {
                n_ContractorStaffIdRequestID: key ? Number(key) : 0,
                s_ContractorEmployeeName: data.contractorEmployeeName,
                s_Nationality: data.nationality,
                s_NationalID: data.nationalId,
                dt_NationalIDExpiryDate: formattedDateTime(new Date(data.nationalIdExpiryDate)),
                dt_DateOfBirth: formattedDateTime(new Date(data.dob)),
                s_ContractorCompanyName: data.contractorsCompanyName,
                s_Department: data.department,
                s_JobPosition: data.jobPosition,
                dt_WorkStartDate: formattedDateTime(new Date(data.workStartDate)),
                s_ResidentialAddress: data.residentialAddress,
                s_ContactNumber: data.contactNumber,
                dt_CardValidityFrom: formattedDateTime(new Date(data.cardValidityFrom)),
                dt_CardValidityUntil: formattedDateTime(new Date(data.cardValidityUntil)),

                s_PersonalPhotographFileBase64: data.uploadPersonalPhoto ? await fileToBase64(data.uploadPersonalPhoto) : "",
                s_PersonalPhotographContentType: data.uploadPersonalPhoto ? data.uploadPersonalPhoto.type : "",
                havePersonalPhotographFile: !!data.uploadPersonalPhoto,

                s_NationalIDCopyFileBase64: data.uploadCopyOfNationalId ? await fileToBase64(data.uploadCopyOfNationalId) : "",
                s_NationalIDCopyContentType: data.uploadCopyOfNationalId ? data.uploadCopyOfNationalId.type : "",
                haveNationalIDCopyFile: !!data.uploadCopyOfNationalId,

                s_AuthorizationLetterFileBase64: data.uploadContractorsAuthorizationLetter ? await fileToBase64(data.uploadContractorsAuthorizationLetter) : "",
                s_AuthorizationLetterContentType: data.uploadContractorsAuthorizationLetter ? data.uploadContractorsAuthorizationLetter.type : "",
                haveAuthorizationLetterFile: !!data.uploadContractorsAuthorizationLetter,
            }

            await AddUpdateIssuanceRenewalIdForStaffFromContractedCompanies(body);

            toast.success(
                key
                    ? t("issuanceRenewalIdUpdatedSuccessfully")
                    : t("issuanceRenewalIdSubmittedSuccessfully")
            );

            setTimeout(() => {
                navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
            }, 700);
        } catch (error) {
            toast.error(t("somethingWentWrong"));
        }
    }, [
        key,
        navigate,
        t,
        AddUpdateIssuanceRenewalIdForStaffFromContractedCompanies,
    ])

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={isAddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesLoading}
                                    disabled={
                                        isAddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}

            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {!key && (
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isAddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesLoading}
                            disabled={
                                isAddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesLoading
                            }
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.ISSUANCE_RENEWAL_ID}
                permitId={issuanceRenewalId?.n_ContractorStaffIdRequestID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.ISSUANCE_RENEWAL_ID}
                permitId={issuanceRenewalId?.n_ContractorStaffIdRequestID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default memo(ActionButtonGroup)
